import type {
  BoundingBox,
  CesiumViewer,
  CesiumViewerType,
} from '@aus-platform/cesium';
import { ZoomMode } from '@aus-platform/cesium';
import type { Cartesian3, Cartographic, Color } from 'cesium';
import {
  BoundingSphere,
  Math as CesiumMath,
  HeadingPitchRange,
  Rectangle,
  ScreenSpaceEventType,
} from 'cesium';
import type { NavigationOptions } from 'cesium-navigation-es6';
import CesiumNavigation from 'cesium-navigation-es6'; // eslint-disable-line import/default
import { isNil, isUndefined } from 'lodash';
import { EnvVariables } from '../env-variables';
import { getCartographicPositionFromMousePosition } from './helpers';
import { CesiumLayerManager } from './managers';
import type { FlyToArguments, CesiumMouseLocationCoordinates } from './types';

// @TODO: Add cesium proxy type interface.
export class CesiumProxy {
  private readonly _cesiumViewer: CesiumViewer;
  private _layerManager: CesiumLayerManager | null;

  private readonly _mouseLocationListeners = new Set<
    (coordinates: CesiumMouseLocationCoordinates) => void
  >();

  private _lastMouseLocation: CesiumMouseLocationCoordinates | null = null;

  private readonly _handleMouseMove = (event: { endPosition: any }) => {
    const position = getCartographicPositionFromMousePosition(
      event.endPosition,
      this.viewer,
    );

    if (isNil(position)) {
      return;
    }

    const { latitude, longitude, altitude } = position;

    const coordinates = {
      latitude,
      longitude,
      altitude,
    };

    this._lastMouseLocation = coordinates;

    this._mouseLocationListeners.forEach((listener) => {
      listener(coordinates);
    });
  };

  constructor(viewer: CesiumViewer) {
    this._cesiumViewer = viewer;
    this._layerManager = new CesiumLayerManager(viewer);
    this.viewer.screenSpaceEventHandler.setInputAction(
      this._handleMouseMove,
      ScreenSpaceEventType.MOUSE_MOVE,
    );

    if (this._cesiumViewer.viewer) {
      if (
        !isUndefined(EnvVariables.environment) &&
        ['development', 'uat'].includes(EnvVariables.environment)
      ) {
        this._cesiumViewer.viewer.scene.debugShowFramesPerSecond = true;
      }

      // Cesium navigation third party tool. ref: https://www.npmjs.com/package/cesium-navigation-es6
      const options: NavigationOptions = {
        enableCompass: true,
        enableZoomControls: false,
        enableDistanceLegend: true,
        enableCompassOuterRing: true,
      };

      new CesiumNavigation(this._cesiumViewer.viewer, options);
    } else {
      console.error('Viewer is not initialized');
    }
  }

  switchTo3D = () => this.cesiumViewer.switchTo3D();

  switchTo2D = () => this.cesiumViewer.switchTo2D();

  setGlobeBaseColor(color: Color) {
    this.viewer.scene.globe.baseColor = color;
  }

  getGlobeHeight(destination: Cartographic) {
    return this.viewer.scene.globe.getHeight(destination);
  }

  setNavigationResetLocation(longitude: string, latitude: string) {
    const navElement = document.getElementsByClassName('navigation-controls');

    navElement
      ?.item(0)
      ?.children.item(1)
      ?.addEventListener('click', () => {
        this.cesiumViewer.flyTo(longitude, latitude);
      });
  }

  flyTo({ longitude, latitude, height, heading, pitch, roll }: FlyToArguments) {
    this._cesiumViewer.flyTo(longitude, latitude, height, heading, pitch, roll);
  }

  flyToBounds(bounds: BoundingBox) {
    const rectangle = Rectangle.fromDegrees(...bounds);

    const boundingSphere = BoundingSphere.fromRectangle3D(rectangle);

    // Pitch: -90 degrees to look straight down.
    const offset = new HeadingPitchRange(0, -CesiumMath.PI_OVER_TWO, 0);

    this.viewer.camera.flyToBoundingSphere(boundingSphere, { offset });
  }

  flyCameraTo(options: {
    destination: Cartesian3 | Rectangle;
    duration?: number;
  }) {
    this.viewer.camera.flyTo(options);
  }

  /**
   * Flies the camera to the default earth view.
   * @param duration duration in seconds in which the camera will fly to default earth view.
   */
  flyHome(duration?: number) {
    this.viewer.camera.flyHome(duration);
  }

  // Navigation controls.
  zoomOut() {
    this.cesiumViewer.zoom(ZoomMode.ZoomOut);
  }

  zoomIn() {
    this.cesiumViewer.zoom(ZoomMode.ZoomIn);
  }

  reset(bounds?: BoundingBox) {
    if (bounds) {
      this.flyToBounds(bounds);
    } else {
      this.flyHome(1.5);
    }
  }

  /**
   * To set mouse location listener using screen space handler.
   */
  addMouseLocationListener(
    listener: (coordinates: CesiumMouseLocationCoordinates) => void,
  ) {
    this._mouseLocationListeners.add(listener);
  }

  removeMouseLocationListener(
    listener: (coordinates: CesiumMouseLocationCoordinates) => void,
  ) {
    this._mouseLocationListeners.delete(listener);
  }

  // Getters.
  get cesiumViewer(): CesiumViewer {
    return this._cesiumViewer;
  }

  get viewer(): CesiumViewerType {
    if (!this._cesiumViewer.viewer) {
      throw new Error('Viewer is not initialized');
    }

    return this._cesiumViewer.viewer;
  }

  get lastMouseLocation() {
    return this._lastMouseLocation;
  }

  get layerManager(): CesiumLayerManager {
    if (!this._layerManager) {
      throw new Error('Layer Manager is not initialized');
    }

    return this._layerManager;
  }

  get drawingTools() {
    return this.viewer.drawingTools;
  }

  get textTool() {
    return this.viewer.textTool;
  }

  get pointCloudTools() {
    return this.viewer.pointCloudTools;
  }

  get cvTilesTools() {
    return this.viewer.cvTilesTools;
  }

  get isDestroyed() {
    return this.viewer.isDestroyed();
  }

  destroy() {
    this._layerManager = null;
    this.viewer.screenSpaceEventHandler.removeInputAction(
      ScreenSpaceEventType.MOUSE_MOVE,
    );
    if (!this.isDestroyed) {
      this._cesiumViewer.viewer?.destroy();
    }
  }
}
